/**
 * Admin Dashboard Page
 *
 * Main dashboard page for platform administrators.
 * Displays ledger health, queue stats, user metrics, and quick actions.
 */

import React, { useEffect, useState } from 'react';
import { getDashboardStats, getRecentJobs } from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { LedgerHealthCard } from '../components/admin/LedgerHealthCard.js';
import { QueueHealthCard } from '../components/admin/QueueHealthCard.js';
import { UserStatsCard } from '../components/admin/UserStatsCard.js';
import { JobStatusTable } from '../components/admin/JobStatusTable.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import { XrplConnectionCard } from '../components/admin/XrplConnectionCard.js';
import { SelfHealingCard } from '../components/admin/SelfHealingCard.js';
import { AnalyticsHealthCard } from '../components/admin/AnalyticsHealthCard.js';
import type { DashboardStatsResponse, RecentJobsResponse } from '../types/admin.js';

export function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [recentJobs, setRecentJobs] = useState<RecentJobsResponse | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDashboardStats();
      setStats(data);
      setLastRefreshed(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentJobs = async () => {
    try {
      const filter = statusFilter !== 'all' ? (statusFilter as 'queued' | 'processing' | 'completed' | 'failed') : undefined;
      const data = await getRecentJobs({ limit: 50, status: filter });
      setRecentJobs(data);
    } catch (err: any) {
      console.error('Failed to load recent jobs:', err);
    }
  };

  useEffect(() => {
    loadDashboardStats();
    loadRecentJobs();

    // Auto-refresh dashboard stats every 10 seconds
    const interval = setInterval(() => {
      loadDashboardStats();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadRecentJobs();
  }, [statusFilter]);

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="mt-1 sm:mt-2 text-sm text-gray-600">
              Platform operational metrics and management tools
            </p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            {lastRefreshed && (
              <div className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                Last updated: {lastRefreshed.toLocaleTimeString()}
              </div>
            )}
            <button
              onClick={loadDashboardStats}
              disabled={loading}
              className="rounded-md bg-blue-600 px-3 sm:px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadDashboardStats} className="mb-6" />
        )}

        {/* Loading State with Skeletons */}
        {loading && !stats && (
          <div className="grid gap-6">
            {/* Ledger Health Skeleton */}
            <div className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            </div>

            {/* Queue Health Skeleton */}
            <div className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="h-8 bg-gray-200 rounded w-24"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="h-8 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            </div>

            {/* User Stats Skeleton */}
            <div className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-20"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-20"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            </div>

            {/* Recent Jobs Skeleton */}
            <div className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
              <div className="space-y-3">
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Content */}
        {stats && (
          <div className="grid gap-6">
            {/* XRPL Connection Management */}
            <XrplConnectionCard onResetComplete={loadDashboardStats} />

            {/* Ledger Health - User Story 1 (Implemented) */}
            <LedgerHealthCard stats={stats.ledgerStats} />

            {/* Analytics Pipeline Health - Volume processing monitor */}
            <AnalyticsHealthCard />

            {/* Queue Monitoring - User Story 5 (Implemented) */}
            <QueueHealthCard queueStats={stats.queueStats} />

            {/* Self-Healing Status - Enrichment Pipeline Recovery */}
            <SelfHealingCard />

            {/* User Statistics - User Story 7 (Implemented) */}
            <UserStatsCard userStats={stats.userStats} />

            {/* Recent Jobs - User Story 6 (Implemented) */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recent Jobs</h2>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                >
                  <option value="all">All Statuses</option>
                  <option value="queued">Queued</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {recentJobs && recentJobs.jobs.length > 0 ? (
                <JobStatusTable
                  jobs={recentJobs.jobs}
                  jobType="unified"
                  emptyMessage="No jobs found"
                />
              ) : (
                <p className="text-sm text-gray-500">No recent jobs found</p>
              )}
            </div>

            {/* Whitelisted Collections - User Story 2 (TODO) */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900">Whitelisted Collections</h2>
              <p className="mt-2 text-sm text-gray-500">
                This section will display list of whitelisted issuer/taxon pairs.
                Implementation pending Phase 4 (User Story 2).
              </p>
              <div className="mt-4">
                <a
                  href="/admin/collections"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  View Collections →
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
